import { useParams, Link } from "react-router-dom";
import { articles, categories } from "@/mocks/articles";
import SEOHead from "@/components/base/SEOHead";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const baseUrl = "https://heiliao.wan.com";

  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-12 min-h-screen bg-background-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100">
              <i className="ri-file-forbid-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-600 mb-4">该文章可能已被删除或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category ? category.name : article.category;
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const pageUrl = `${baseUrl}/article/${article.id}`;

  const schemaJson = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: baseUrl },
          { "@type": "ListItem", position: 2, name: categoryName, item: `${baseUrl}/category/${article.category}` },
          { "@type": "ListItem", position: 3, name: article.title },
        ],
      },
      {
        "@type": "Article",
        "@id": `${pageUrl}/#article`,
        headline: article.title,
        description: article.excerpt,
        author: { "@type": "Person", name: article.author },
        datePublished: article.publishDate,
        image: article.imageUrl,
        url: pageUrl,
        publisher: { "@id": `${baseUrl}/#organization` },
      },
    ],
  };

  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} - 黑料wan网`}
        description={article.excerpt}
        keywords={article.tags.join(",")}
        canonicalUrl={pageUrl}
        ogType="article"
        ogImage={article.imageUrl}
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-12 md:pb-16 bg-background-50 min-h-screen">
        <article className="w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-user-line"></i> {article.author}
                </span>
                <span className="whitespace-nowrap">{article.publishDate}</span>
                <span className="whitespace-nowrap">{article.readTime}阅读</span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-eye-line"></i>
                  {article.viewCount >= 10000 ? `${(article.viewCount / 10000).toFixed(1)}万次` : `${article.viewCount}次`}浏览
                </span>
                <span className="flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-message-2-line"></i> {article.commentCount}条评论
                </span>
              </div>
            </header>

            <div className="relative w-full h-64 md:h-96 rounded-xl overflow-hidden mb-8">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-lg max-w-none text-foreground-800 leading-relaxed article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <section className="mt-12 pt-8 border-t border-background-200">
                <h2 className="text-xl font-bold text-foreground-950 mb-6" style={{ fontFamily: "var(--font-heading)" }}>
                  相关爆料
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {relatedArticles.map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}`}
                      className="group bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="relative h-40 overflow-hidden">
                        <img
                          src={related.imageUrl}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={related.title}
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-foreground-950 font-semibold text-sm line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                          {related.title}
                        </h3>
                        <p className="text-foreground-500 text-xs mt-2 whitespace-nowrap">{related.publishDate}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}